import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of, tap, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { IUser } from '../user';
import { UserService } from '../../services';
import { WwwRouteSlug } from '../enums';
import { Navigate } from '@ngxs/router-plugin';
import { isPlatformServer } from '@angular/common';

export class WwwAppStateLogout {
  public static readonly type = '[WwwAppState] logout';
}

export class WwwAppStateClearUserData {
  public static readonly type = '[WwwAppState] clear user data';
}

export class WwwAppStateGetUserData {
  public static readonly type = '[WwwAppState] get user data';
}

export class WwwAppStateSetUserData {
  public static readonly type = '[WwwAppState] set user data';

  constructor(public readonly payload: { user: IUser }) {}
}

export interface WwwAppStateModel {
  loading: boolean;
  cookiesAllowed: boolean | null;
  user: IUser | null;
}

@State<WwwAppStateModel>({
  name: 'wwwApp',
  defaults: {
    loading: false,
    cookiesAllowed: null,
    user: null,
  },
})
@Injectable()
export class WwwAppState {
  constructor(
    private readonly _userService: UserService,
    @Inject(PLATFORM_ID)
    private readonly _platformId: object,
  ) {}

  @Selector()
  public static loading({ loading }: WwwAppStateModel): boolean {
    return loading;
  }

  @Selector()
  public static user({ user }: WwwAppStateModel): IUser | null {
    return user;
  }

  @Action(WwwAppStateSetUserData)
  public setUserData(
    { patchState }: StateContext<WwwAppStateModel>,
    { payload: { user } }: WwwAppStateSetUserData,
  ): void {
    patchState({
      user,
    });
  }

  @Action(WwwAppStateGetUserData)
  public getUserData({ patchState }: StateContext<WwwAppStateModel>): Observable<IUser> {
    patchState({ loading: true });
    return this._userService.getMe().pipe(
      tap((user) => {
        patchState({
          user,
        });
      }),
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }

  @Action(WwwAppStateLogout)
  public logout({ patchState, dispatch }: StateContext<WwwAppStateModel>): Observable<void | null> {
    if (isPlatformServer(this._platformId)) {
      return of(null);
    }
    const jwtHp = document.cookie
      ?.split(';')
      ?.find((item) => item.includes('jwt_hp'))
      ?.split('=')[1];
    if (!jwtHp) {
      patchState({ user: null });
      dispatch(new Navigate([WwwRouteSlug.LOGIN]));
      return of(null);
    }
    patchState({ loading: true });
    return this._userService.logout().pipe(
      tap(() => {
        patchState({ user: null, loading: false });
        dispatch(new Navigate([WwwRouteSlug.LOGIN]));
      }),
      catchError((err) => {
        patchState({ user: null, loading: false });
        dispatch(new Navigate([WwwRouteSlug.LOGIN]));
        return throwError(() => err);
      }),
    );
  }

  @Action(WwwAppStateClearUserData)
  public clearUserData({ patchState }: StateContext<WwwAppStateModel>): void {
    patchState({ user: null });
  }
}
